/**
 * ListView — Kanban-style columns grouped by tag
 * Reads from the same node data as the canvas view
 */

export default function ListView({ nodes, zones, onNodeClick, filter }) {
  // Group non-zone nodes by their first tag
  const tagged = {};
  const untagged = [];

  // Create a column for each zone
  zones.forEach(z => { tagged[z.data.label] = { color: z.data.color, items: [] }; });

  nodes.filter(n => n.type !== 'zone').forEach(node => {
    const tag = node.data?.tags?.[0];
    const dimmed = filter && !matchesFilter(node, filter);
    const item = { ...node, dimmed };
    if (tag && tagged[tag]) {
      tagged[tag].items.push(item);
    } else {
      untagged.push(item);
    }
  });

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex gap-4 min-h-full">
        {Object.entries(tagged).map(([label, { color, items }]) => (
          <div key={label} className="flex-shrink-0 w-64 bg-gray-50 rounded-xl border border-gray-200 flex flex-col max-h-[calc(100vh-140px)]">
            <div className="px-3 py-2.5 border-b border-gray-200 flex items-center gap-2 flex-shrink-0">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
              <span className="text-sm font-semibold text-gray-700">{label}</span>
              <span className="text-[10px] text-gray-400 ml-auto">{items.length}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {items.map(item => (
                <div key={item.id}
                  onClick={() => onNodeClick?.(item.id)}
                  className={`rounded-lg p-3 cursor-pointer transition-opacity border ${item.dimmed ? 'opacity-20' : 'hover:shadow-md'}`}
                  style={{ backgroundColor: item.data?.color || '#fff', borderColor: item.dimmed ? 'transparent' : '#e5e7eb' }}>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap leading-snug">
                    {item.data?.content || <span className="text-gray-400 italic">Empty</span>}
                  </div>
                  <div className="text-[10px] text-gray-400 mt-1.5">{item.type}</div>
                </div>
              ))}
              {items.length === 0 && (
                <div className="text-center text-xs text-gray-400 py-6">No items</div>
              )}
            </div>
          </div>
        ))}
        {untagged.length > 0 && (
          <div className="flex-shrink-0 w-64 bg-gray-50 rounded-xl border border-gray-200 flex flex-col max-h-[calc(100vh-140px)]">
            <div className="px-3 py-2.5 border-b border-gray-200">
              <span className="text-sm font-semibold text-gray-500">Untagged</span>
              <span className="text-[10px] text-gray-400 ml-2">{untagged.length}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {untagged.map(item => (
                <div key={item.id} onClick={() => onNodeClick?.(item.id)}
                  className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer hover:shadow-md">
                  <div className="text-sm text-gray-800">{item.data?.content || 'Empty'}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function matchesFilter(node, filter) {
  if (!filter) return true;
  const q = filter.toLowerCase();
  if (node.data?.tags?.some(t => t.toLowerCase().includes(q))) return true;
  if (node.data?.content?.toLowerCase().includes(q)) return true;
  return false;
}
